import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Info, Eye } from '@phosphor-icons/react';

import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import NoDataFallback from '../../components/common/NoDataFallback';
import { useAgents } from '../../hooks/useAgents';
import { getWhatsappClientsChat } from '../../utils/apis';
import theme from '../../theme';

// Types
interface Agent {
  _id: string;
  agentName: string;
  agentChannel: string;
  whatsappPhonenumberId?: string;
  config: {
    whatsAppDetails: {
      whatsappPhonenumberId: string;
    };
  };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  prompt_tokens?: number;
  completion_tokens?: number;
}

interface ChatItem {
  _id: string;
  phoneNumber: string;
  customer_name?: string;
  chat: ChatMessage[];
}

interface TokenStats {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

// Constants
const SELECTED_AGENT_KEY = 'selectedWhatsappAgent';

const calculateTokens = (chat: ChatMessage[]): TokenStats => {
  if (!Array.isArray(chat)) {
    return { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
  }

  const stats = chat.reduce(
    (acc, message) => {
      if (message.role === 'user' && message.prompt_tokens) {
        acc.promptTokens += message.prompt_tokens;
      }
      if (message.role === 'assistant' && message.completion_tokens) {
        acc.completionTokens += message.completion_tokens;
      }
      return acc;
    },
    { promptTokens: 0, completionTokens: 0 }
  );

  return {
    ...stats,
    totalTokens: stats.promptTokens + stats.completionTokens,
  };
};

const getStoredAgent = (): Agent | null => {
  try {
    const stored = localStorage.getItem(SELECTED_AGENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const storeAgent = (agent: Agent): void => {
  try {
    localStorage.setItem(SELECTED_AGENT_KEY, JSON.stringify(agent));
  } catch (error) {
    console.warn('Failed to store agent in localStorage:', error);
  }
};

const WhatsAppLogs = () => {
  const navigate = useNavigate();
  const { agents } = useAgents();

  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  console.log('selectedAgent', selectedAgent);

  // Memoize filtered WhatsApp agents
  const whatsappAgents = useMemo(() => {
    return (
      agents?.agents?.filter(
        (agent: Agent) =>
          agent.agentName.trim() !== '' && agent.agentChannel === 'whatsapp'
      ) || []
    );
  }, [agents?.agents]);

  // Initialize selected agent
  useEffect(() => {
    if (whatsappAgents.length === 0) return;

    const storedAgent = getStoredAgent();
    const validStoredAgent =
      storedAgent &&
      whatsappAgents.some((agent: Agent) => agent._id === storedAgent._id);

    if (validStoredAgent) {
      setSelectedAgent(storedAgent);
    } else {
      const firstAgent = whatsappAgents[0];
      setSelectedAgent(firstAgent);
      storeAgent(firstAgent);
    }
  }, [whatsappAgents]);

  const handleAgentChange = useCallback(
    (event: SelectChangeEvent) => {
      const agent = whatsappAgents.find(
        (agent: Agent) => agent._id === event.target.value
      );
      if (agent) {
        setSelectedAgent(agent);
        storeAgent(agent);
      }
    },
    [whatsappAgents]
  );

  const handleViewChats = useCallback(
    (phoneNumber: string) => {
      if (selectedAgent?.config.whatsAppDetails.whatsappPhonenumberId) {
        navigate(
          `/whatsapp-chats/${selectedAgent.config.whatsAppDetails.whatsappPhonenumberId}/${phoneNumber}`,
          {
            state: {
              agent: selectedAgent,
            },
          }
        );
      }
    },
    [navigate, selectedAgent]
  );

  // Fetch WhatsApp clients chat data
  const {
    data: whatsappClientsChat,
    isLoading: isWhatsappClientsChatLoading,
    error: whatsappClientsChatError,
  } = useQuery({
    queryKey: [
      'whatsappClientsChat',
      selectedAgent?.config?.whatsAppDetails?.whatsappPhonenumberId,
    ],
    queryFn: () => {
      const phoneNumberId =
        selectedAgent?.config?.whatsAppDetails?.whatsappPhonenumberId;
      if (!phoneNumberId) {
        throw new Error('WhatsApp phone number ID is not available');
      }
      return getWhatsappClientsChat(phoneNumberId);
    },
    enabled: !!selectedAgent?.config?.whatsAppDetails?.whatsappPhonenumberId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Transform data for DataGrid
  const tableData = useMemo(() => {
    const chatData = whatsappClientsChat?.data?.data || [];
    return chatData.map((item: ChatItem, index: number) => {
      const tokens = calculateTokens(item.chat);
      return {
        ...item,
        id: item._id || `${item.phoneNumber}-${index}`,
        phoneNumber: item.phoneNumber,
        customerName: item.customer_name || '-',
        whatsappPhonenumberId:
          selectedAgent?.config.whatsAppDetails.whatsappPhonenumberId || '',
        promptTokens: tokens.promptTokens,
        completionTokens: tokens.completionTokens,
        totalTokens: tokens.totalTokens,
        messageCount: item.chat.length,
      };
    });
  }, [
    whatsappClientsChat?.data?.data,
    selectedAgent?.config.whatsAppDetails.whatsappPhonenumberId,
  ]);

  // DataGrid columns configuration
  const columns: GridColDef[] = [
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      headerClassName: 'data-grid-header',
      flex: 1,
      minWidth: 150,
      filterable: true,
      renderCell: params => (
        <Typography variant="body2" sx={{ fontWeight: 400 }}>
          +{params.value}
        </Typography>
      ),
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      headerClassName: 'data-grid-header',
      flex: 1.2,
      minWidth: 160,
      filterable: true,
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            color: params.value === 'N/A' ? '#9CA3AF' : 'inherit',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'whatsappPhonenumberId',
      headerName: 'WhatsApp Phone ID',
      headerClassName: 'data-grid-header',
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: params => (
        <Typography variant="body2" sx={{ fontSize: '14px' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'promptTokens',
      headerName: 'Prompt Tokens',
      headerClassName: 'data-grid-header',
      flex: 1,
      minWidth: 140,
      filterable: true,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            Prompt Tokens
          </Typography>
          <Tooltip
            title="Prompt tokens represent the number of tokens used in the user's messages sent to the AI model."
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <Info size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      valueFormatter: (params: number) => params.toLocaleString(),
    },
    {
      field: 'completionTokens',
      headerName: 'Completion Tokens',
      headerClassName: 'data-grid-header',
      flex: 1.2,
      minWidth: 160,
      filterable: true,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            Completion Tokens
          </Typography>
          <Tooltip
            title="Completion tokens represent the number of tokens used in the AI model's responses."
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <Info size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      valueFormatter: (params: number) => params.toLocaleString(),
    },
    {
      field: 'totalTokens',
      headerName: 'Total Tokens',
      headerClassName: 'data-grid-header',
      flex: 1,
      minWidth: 130,
      filterable: true,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Typography variant="body2" sx={{ fontWeight: 400 }}>
          {params.value.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Chat Details',
      headerClassName: 'data-grid-header',
      align: 'center',
      headerAlign: 'center',
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: params => (
        <Tooltip title="View Details">
          <IconButton
            onClick={() => handleViewChats(params.row.phoneNumber)}
            size="small"
          >
            <Eye />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (whatsappClientsChatError) {
    return (
      <LayoutWithSidebar margin={2}>
        <NoDataFallback
          message="No chats found"
          description="There are no chats available for this agent"
        />
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar margin={2}>
      <Box
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <HeaderContainer
          sx={{
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{ fontSize: '24px', fontWeight: 600 }}
            >
              WhatsApp Chats
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                color={theme.palette.lightGray.text}
                fontSize="13px"
                fontWeight={400}
                lineHeight="normal"
                letterSpacing="-0.12px"
              >
                Select Agent
              </Typography>
              <Select
                value={selectedAgent?._id || ''}
                onChange={handleAgentChange}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '8px 12px',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid #DCE0E7',
                    background: '#F8F9FC',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  minWidth: '140px',
                }}
                disabled={whatsappAgents.length === 0}
              >
                {whatsappAgents.map((agent: Agent) => (
                  <MenuItem
                    key={agent._id}
                    value={agent._id}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {agent.agentName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </HeaderContainer>

        <Box
          sx={{
            flexGrow: 1,
            height: 'calc(100vh - 120px)',
            minHeight: 400,
            width: '100%',
            bgcolor: '#fff',
          }}
        >
          <DataGrid
            rows={tableData}
            columns={columns}
            getRowId={row => row.id || `${row.phoneNumber}-${Date.now()}`}
            loading={isWhatsappClientsChatLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 75, 100]}
            disableRowSelectionOnClick
            disableColumnFilter={false}
            disableColumnSelector={false}
            disableDensitySelector={false}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: `1px solid ${theme.palette.lightGray.main}`,
              borderRadius: '12px',
              height: '100%',
              flex: 1,
              '& .data-grid-header': {
                bgcolor: '#F8F9FC',
                color: 'custom.textBlue',
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 16px',
              },
              '& .MuiDataGrid-cell': {
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                '&:focus, &:focus-within': {
                  outline: 'none !important',
                },
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none !important',
              },
            }}
          />
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default WhatsAppLogs;
